import { Box, Grid } from "@mui/material";
import type { Dayjs } from "dayjs";
import AsyncSelect, { AsyncSelectValue } from "../AsyncSelect";
import CustomDatePicker from "../CustomDatePicker";
import CustomSelect from "../CustomSelect";
import Input from "../Input";
import MaskMultiSelect from "../MaskMultiSelect";
import { Filter, INVALID_DATE_MESSAGE } from "./filter-constant";
import { isInvalidDate } from "@/utils/date-time-utility";

export type DateRangePresetValue = {
  date_range?: string | null;
  startDate?: string | Date | Dayjs | null;
  endDate?: string | Date | Dayjs | null;
};

export const getComponent = (
  value: string | AsyncSelectValue[] | DateRangePresetValue | undefined,
  onChange: (
    value: string | AsyncSelectValue[] | DateRangePresetValue | undefined
  ) => void,
  type: Filter["type"],
  options?: any[],
  placeholder?: string,
  onSearch?: (value: string) => void,
  name?: string,
  maxDate?: Dayjs,
  minDate?: Dayjs
) => {
  const presetVal =
    type === "date_range_preset"
      ? ((value as DateRangePresetValue | undefined) ?? {})
      : null;

  // [@see: Add custom component support as per requirement]
  const ComponentMapper = {
    custom_select: (
      <CustomSelect
        placeholder={placeholder}
        onSearch={(_, v) => onSearch?.(v)}
        name={name}
        options={options}
        value={value as any}
        onChange={(opt) => onChange(opt ?? "")}
      />
    ),
    multi_select: (
      <MaskMultiSelect
        options={options}
        optionWithSelectAll
        optionWithCheckbox={true}
        value={value as any[]}
        onChange={(v) => onChange(v ?? undefined)}
      />
    ),
    select: (
      <AsyncSelect
        placeholder={placeholder}
        onSearch={onSearch}
        name={name}
        options={options}
        value={value || null}
        onChange={(opt) => onChange(opt)}
      />
    ),
    text: (
      <Input
        textFieldProps={{
          name: "transactionId",
          value: value,
          onChange: (e) => onChange(e.target.value),
          placeholder: placeholder,
        }}
      />
    ),
    date: (
      <CustomDatePicker
        value={value}
        onChange={(val) => {
          onChange(val?.toISOString() ?? "");
        }}
        placeholder={placeholder}
        maxDate={maxDate}
        minDate={minDate}
      />
    ),
    date_range_preset: (
      <Box>
        <CustomSelect
          value={presetVal?.date_range ?? undefined}
          options={options ?? []}
          onChange={(v) =>
            onChange({
              ...presetVal,
              date_range: v ?? undefined,
              startDate: undefined,
              endDate: undefined,
            })
          }
        />
        {presetVal?.date_range === "CUSTOM_RANGE" && (
          <Grid container sx={{ mt: 1 }} spacing={1}>
            <Grid item xs={12}>
              <CustomDatePicker
                label="Start Date"
                value={presetVal?.startDate ?? undefined}
                error={
                  isInvalidDate(presetVal?.startDate)
                    ? INVALID_DATE_MESSAGE
                    : undefined
                }
                onChange={(val) =>
                  onChange({ ...presetVal, startDate: val ?? undefined })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <CustomDatePicker
                label="End Date"
                value={presetVal?.endDate ?? undefined}
                error={
                  isInvalidDate(presetVal?.endDate)
                    ? INVALID_DATE_MESSAGE
                    : undefined
                }
                onChange={(val) =>
                  onChange({ ...presetVal, endDate: val ?? undefined })
                }
              />
            </Grid>
          </Grid>
        )}
      </Box>
    ),
  };

  return ComponentMapper[type];
};
